import { useCallback, useState } from 'react';
import { generatePath } from 'react-router';
import browserHistory from '~/browserHistory';
import { useNavigationDetector } from '~/hooks';
import { routes } from '~/routes';

/**
 * Guards against leaving the Confirm Candidate screen without confirming. While
 * candidates are still available it intercepts navigation: same-job / candidate
 * profile / this shift's cancellation-feedback routes pass through, anything else
 * opens the "Candidate Not Confirmed" exit popup. Also exposes a temporary
 * unblock for intentional navigations.
 */
export const useConfirmExitNavigation = ({ jobId, isError, hasCandidatesAvailable }) => {
  const [confirmationExitModalOpen, setConfirmationExitModalOpen] = useState(false);
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);

  const { allowNavigation, blockNavigation, clearPendingNavigation, navigateToPending } =
    useNavigationDetector({
      resetKey: isError,
      when: hasCandidatesAvailable && shouldBlockNavigation,
      onNavigationAttempt: (targetLocation) => {
        // Check if navigation is to another candidate on the same job or to the candidate profile screen
        const candidateScreen = `/dashboard/candidate/${jobId}`;
        const currentJobPattern = `/dashboard/job/${jobId}`;
        // The cancellation flow for this shift (GOTU-14996): `useCancelJobMutation`
        // redirects here after a cancel from the redesigned page, and a cancelled
        // shift has no candidate left to warn about. Exact match, so another job's
        // feedback screen stays guarded.
        const feedbackScreen = generatePath(routes.cancellationFeedback, { jobId });
        const targetPath = targetLocation.pathname;

        // If navigating to another candidate on the same job or to the candidate profile screen, allow it
        if (
          targetPath.startsWith(currentJobPattern) ||
          targetPath.startsWith(candidateScreen) ||
          targetPath === feedbackScreen
        ) {
          allowNavigation();
          browserHistory.push(targetLocation.pathname, targetLocation.state);
          return;
        }

        // Otherwise, show the confirmation popup
        setConfirmationExitModalOpen(true);
        blockNavigation();
      },
    });

  const handleNavigateWithoutBlocking = useCallback(
    (path) => {
      // Disable blocking temporarily
      setShouldBlockNavigation(false);
      clearPendingNavigation();

      // Navigate after ensuring the blocker is disabled
      setTimeout(() => {
        browserHistory.push(path);
        // Re-enable blocking after navigation (in case user navigates back)
        setTimeout(() => {
          setShouldBlockNavigation(true);
        }, 100);
      }, 0);
    },
    [clearPendingNavigation],
  );

  return {
    allowNavigation,
    clearPendingNavigation,
    navigateToPending,
    handleNavigateWithoutBlocking,
    confirmationExitModalOpen,
    setConfirmationExitModalOpen,
  };
};
